import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Home, Mail, Menu, X } from 'lucide-react';
import AuthPopup from './AuthPopup';
import PaymentPopup from './PaymentPopup';
import { useNavigate } from 'react-router-dom';

const MEDIA_BASE_URL = "https://media.satoshiadvance.com/images";

const BitWheelPage = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(15);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        menuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [menuOpen]);

  const sections = [
    {
      image: `${MEDIA_BASE_URL}/RetroBitWheel1.png`,
      title: "Bitwheel - The Ultimate Spinning Strategy Game",
      quote: "Where guessing meets strategy in blockchain gaming",
      content: "Welcome to Bitwheel, the revolutionary spinning game that combines statistics, strategy, and pure excitement. Whether you're flying solo, competing with friends, or challenging our AI opponent, Bitwheel delivers endless entertainment and real rewards. Earn Bitwheel points with every spin and watch your achievements grow as you master the art of prediction and timing."
    },
    {
      image: `${MEDIA_BASE_URL}/RetroBitWheel2.png`,
      title: "Relaxing Yet Thrilling Gameplay for Everyone",
      quote: "Simple to start, impossible to put down",
      content: "Bitwheel offers the perfect balance of relaxation and excitement. The gameplay is beautifully simple: buy spin points, make your guess, and spin to win. But beneath this simplicity lies a world of strategy and statistics. Learn to read patterns, develop your intuition, and perfect your timing. Whether you want a quick casual game or an intense competitive session, Bitwheel adapts to your mood and play style."
    },
    {
      image: `${MEDIA_BASE_URL}/RetroBitWheel3.png`,
      title: "Multiple Game Modes for Maximum Fun",
      quote: "Solo challenges, friendly competition, and AI battles",
      content: "Experience Bitwheel your way with three exciting game modes. Challenge yourself in solo play and climb the leaderboard by beating your personal best. Invite friends for multiplayer action and see who has the best instincts and strategy. Or face off against our intelligent computer opponent that adapts to your play style. Every mode rewards you with Bitwheel points that unlock amazing prizes."
    },
    {
      image: `${MEDIA_BASE_URL}/RetroLandscape1.png`,
      title: "Earn Points, Unlock Rewards, Win Big",
      quote: "Every spin brings you closer to incredible prizes",
      content: "In Bitwheel, your gameplay translates directly into rewards. Earn Bitwheel points with every spin and redeem them for exclusive retro Bit Dogs merchandise at the Dog Store. Design custom dog hats, t-shirts, bags, towels, and satchels featuring your favorite dog breeds. Plus, when the community reaches 1,000,000 total points, one lucky player wins $500 in free merchandise of their choice!"
    },
    {
      image: `${MEDIA_BASE_URL}/RetroLandscape2.png`,
      title: "Join the Race to 1 Million Points",
      quote: "Community milestone, individual glory",
      content: "The Bitwheel community is on an epic journey to 1,000,000 total Bitwheel points. Every player contributes to this exciting milestone, and when we reach it, one random member will be selected to receive $500 worth of free merchandise from the Dog Store. Sign up with your email to get extra free spins, track your progress on the leaderboard, and increase your chances of being the big winner."
    },
    {
      image: `${MEDIA_BASE_URL}/RetroLandscape3.png`,
      title: "Blockchain Points with Real Future Value",
      quote: "Gaming today, investing for tomorrow",
      content: "Bitwheel is built on cutting-edge blockchain technology, making your points more than just game currency. Every Bitwheel point you earn is securely stored on the blockchain. In the future, these points may be redeemable for actual satoshis, meaning the spin points you buy today could grow in value tomorrow. It's entertainment that could become a real investment."
    },
    {
      image: `${MEDIA_BASE_URL}/RetroSubscribe1.png`,
      title: "Create & Customize Your Bit Dogs Collection",
      quote: "Learn, create, and celebrate dog breeds",
      content: "The Bit Store is your creative hub for dog lovers worldwide. Generate unique random dog cards featuring breeds from all corners of the globe. Learn fascinating facts about each breed, name your digital dogs, write custom descriptions, and assign fun trick commands like sit, play, and roll over. Save your favorites, download your creations, or use your Bitwheel points to order custom-printed merchandise. Perfect for dog enthusiasts who want to combine learning with creativity."
    }
  ];

  const handleHomeClick = () => {
    window.location.href = '/';
  };

  const HeaderBar = () => (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        width: '100%',
        backgroundColor: '#fff',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          position: 'relative',
        }}
      >
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
          onClick={handleHomeClick}
        >
          <Home size={20} color="#0066cc" />
        </motion.div>

        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
          }}
        >
          <span
            style={{
              fontWeight: '600',
              fontSize: '1.1rem',
              color: '#0066cc',
            }}
          >
            Bitwheel
          </span>
        </div>

        <div style={{ marginLeft: 'auto' }}>
          <motion.div
            ref={buttonRef}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '50%',
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
            }}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? (
              <X size={20} strokeWidth={2} color="#0066cc" />
            ) : (
              <Menu size={20} strokeWidth={2} color="#0066cc" />
            )}
          </motion.div>

          {menuOpen && (
            <motion.div
              ref={menuRef}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                width: isDesktop ? '250px' : '200px',
                backgroundColor: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderRadius: '0 0 8px 8px',
                overflow: 'hidden',
                zIndex: 1000,
              }}
            >
              <motion.div
                whileHover={{ backgroundColor: '#f5f5f5' }}
                style={{
                  padding: '1rem 1.5rem',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onClick={() => {
                  navigate('/about');
                  setMenuOpen(false);
                }}
              >
                About
              </motion.div>

              <motion.div
                whileHover={{ backgroundColor: '#f5f5f5' }}
                style={{
                  padding: '1rem 1.5rem',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onClick={() => {
                  navigate('/info-center');
                  setMenuOpen(false);
                }}
              >
                News & Blogs
              </motion.div>

              <motion.div
                whileHover={{ backgroundColor: '#f5f5f5' }}
                style={{
                  padding: '1rem 1.5rem',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={() => {
                  setShowPaymentPopup(true);
                  setMenuOpen(false);
                }}
              >
                Subscribe
              </motion.div>

              <motion.div
                whileHover={{ backgroundColor: '#f5f5f5' }}
                style={{
                  padding: '1rem 1.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
                onClick={() => {
                  setShowAuthPopup(true);
                  setMenuOpen(false);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#0066cc"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                Login / Sign Up
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );

  const ActionButtonsSection = () => (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '1rem',
        margin: isDesktop ? '3rem auto 2rem' : '2rem auto',
        maxWidth: isDesktop ? '800px' : '100%',
        alignItems: 'center'
      }}
    >
      <motion.a
        href="https://wheel.satoshiadvance.com"
        target="_blank"
        rel="noopener noreferrer"
        whileHover={{ scale: 1.05, backgroundColor: '#00b300' }}
        whileTap={{ scale: 0.95 }}
        style={{
          display: 'inline-block',
          backgroundColor: '#00cc00',
          color: 'white',
          padding: isDesktop ? '1.25rem 2rem' : '1rem 1.5rem',
          borderRadius: '12px',
          fontSize: isDesktop ? '1.2rem' : '1.4rem',
          fontWeight: '700',
          textDecoration: 'none',
          boxShadow: '0 6px 16px rgba(0, 204, 0, 0.3)'
        }}
      >
        Play Bitwheel Now
      </motion.a>

      <motion.a
        href="mailto:contact@satoshiadvance.com"
        whileHover={{ scale: 1.05, backgroundColor: '#005bb7' }}
        whileTap={{ scale: 0.95 }}
        style={{
          display: 'inline-block',
          backgroundColor: '#0066cc',
          color: 'white',
          padding: isDesktop ? '1rem 1.5rem' : '0.75rem 1.25rem',
          borderRadius: '12px',
          fontSize: isDesktop ? '1rem' : '1.2rem',
          fontWeight: '600',
          textDecoration: 'none',
          boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)'
        }}
      >
        Contact
      </motion.a>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{
          backgroundColor: '#0066cc',
          color: 'white',
          padding: isDesktop ? '1rem 1.5rem' : '0.75rem 1.25rem',
          borderRadius: '12px',
          fontSize: isDesktop ? '1rem' : '1.2rem',
          fontWeight: '600',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)'
        }}
        onClick={() => setShowAuthPopup(true)}
      >
        Login/Signup
      </motion.button>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{
          backgroundColor: '#0066cc',
          color: 'white',
          padding: isDesktop ? '1rem 1.5rem' : '0.75rem 1.25rem',
          borderRadius: '12px',
          fontSize: isDesktop ? '1rem' : '1.2rem',
          fontWeight: '600',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)'
        }}
        onClick={() => setShowPaymentPopup(true)}
      >
        Subscribe
      </motion.button>
    </div>
  );

  const Section = ({ image, title, quote, content }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '10px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
          marginBottom: '2rem',
          textAlign: 'left',
          maxWidth: '800px',
          margin: '0 auto 2rem',
          minHeight: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <img
          src={image.startsWith('http') ? image : `/images/${image}`}
          alt={title}
          referrerPolicy="no-referrer"
          style={{
            width: '95%',
            height: 'auto',
            borderRadius: '10px',
            marginBottom: '1rem',
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        />
        <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center' }}>
          {title}
        </h2>
        <blockquote style={{ fontStyle: 'italic', marginBottom: '1rem', color: '#666', textAlign: 'center' }}>
          {quote}
        </blockquote>
        <p style={{ fontSize: '1.2rem', lineHeight: '1.8', textAlign: 'center' }}>{content}</p>
      </motion.div>
    );
  };

  return (
    <div
      id="top"
      style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '2rem',
        overflowY: 'auto',
      }}
    >
      <HeaderBar />
      <div style={{ height: isDesktop ? '80px' : '60px' }}></div>

      {sections.map((section, index) => (
        <Section key={index} {...section} />
      ))}

      <ActionButtonsSection />

      <div style={{ textAlign: 'center', marginTop: '2rem', marginBottom: '2rem' }}>
        <a
          href="#top"
          style={{
            textDecoration: 'none',
            color: '#0066cc',
            fontWeight: 'bold',
            fontSize: isDesktop ? '1.2rem' : '1rem'
          }}
        >
          Back to Top
        </a>
      </div>

      {showAuthPopup && <AuthPopup onClose={() => setShowAuthPopup(false)} />}
      {showPaymentPopup && (
        <PaymentPopup
          onClose={() => setShowPaymentPopup(false)}
          amount={paymentAmount}
        />
      )}
    </div>
  );
};

export default BitWheelPage;
